import {ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {MainComponent} from '@core/main.component';
import {environment} from '@environments/environment';
import {Platform} from '@ionic/angular';
import {register} from 'swiper/element/bundle';
import {CordovaService} from '@services/cordova.service';
import {CatalogService} from '@models/catalog.service';
import {ModalService} from '@services/modal.service';
import {SurveyService} from '@models/survey.service';
import {DeepLinkService} from '@services/deep-link.service';
import {PushModalComponent} from '@components/modals/push-modal/item';
import {UpdateModalComponent} from '@components/modals/update-modal/item';
import {InAppBrowser} from '@awesome-cordova-plugins/in-app-browser';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent extends MainComponent implements OnInit {
  platform = inject(Platform);
  cordovaService = inject(CordovaService);
  catalogService = inject(CatalogService);
  surveyService = inject(SurveyService);
  cdr = inject(ChangeDetectorRef);
  menuItems = [];
  private update: { needUpdate: boolean; version: any } = null;
  private modalService = inject(ModalService);
  private deepLinkService = inject(DeepLinkService);
  private appIsOpen = false;
  private appIsOpenTime = 0;
  private appIsOpenInterval = null;
  startupLoading = false;
  private pushStarted = false;

  constructor() {
    super();
  }

  ngOnInit() {
    console.log('[APP_INIT] ngOnInit: Start');
    // если приложение было свёрнуто, то очищаем интервал, appIsOpen и appIsOpenTime
    this.platform.pause.subscribe(() => {
      this.appIsOpen = false;
      this.appIsOpenTime = 0;
      clearInterval(this.appIsOpenInterval);
    });

    // если приложение возвращено из свёрнутого режима - запускаем startCheckOpenApp
    this.platform.resume.subscribe(() => {
      this.startCheckOpenApp();
    });
    this.initializeApp();
  }

  async initializeApp() {
    if (this.startupLoading) { return; }
    this.startupLoading = true;
    console.log('[APP_INIT] initializeApp: Start');
    this.appService.setTitle(environment.title);

    try {
    // Some native plugins can prevent Cordova from emitting deviceready.
    // Never leave the startup view waiting forever in that case.
    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => reject(new Error('Cordova deviceready timed out')), 15000);
      this.platform.ready().then(() => {
        clearTimeout(timeoutId);
        resolve();
      }, error => {
        clearTimeout(timeoutId);
        reject(error);
      });
    });
    // Show the web view while startup requests run, including on failure.
    // @ts-ignore
    navigator.splashscreen?.hide();
    console.log('[APP_INIT] initializeApp: Platform ready');

    if (!this.appService.isDevice()) {
      console.log('[APP_INIT] initializeApp: Not on a device');
      this.appService.deviceId = 'browsertoken';
    } else {
       console.log('[APP_INIT] initializeApp: Running on a device (Cordova)');
    }

    if (this.appService.isCordova() && !this.pushStarted) {
      this.pushStarted = true;
      console.log('[APP_INIT] initializeApp: Cordova detected. Starting checks and push setup.');
      this.startCheckOpenApp();
      console.log('[APP_INIT] initializeApp: startCheckOpenApp called');

      this.appService.subscribePush(() => {
        console.log('[APP_INIT] initializeApp: subscribePush callback executed (token received?)');
        // Здесь можно добавить логику, которая должна выполниться сразу после получения токена
      }, (data) => {
        console.log('[APP_INIT] initializeApp: subscribePush callbackPush executed (message received)', data);
        // Обработка входящих пушей, как было
        if (data) {
          // показываем модалку если приложение в момент получения пуша открыто
          // или data.show === '1'
          if (data.show === '1' || this.appIsOpen) {
            this.modalService.makeOpenModal({
              component: PushModalComponent,
              showBackdrop: true,
              componentProps: {title: data.title, text: data.text, confirmText: data.buttonText},
              cssClass: `modal-select`,
              mode: 'md',
            }, (answer) => {});
          }
          if (data.path) {
            // this.navigationService.goTo(data.path);
            this.deepLinkService.openDeepLink(data);
          }
        }
      }).catch(error => {
        console.error('[APP_INIT] initializeApp: subscribePush failed with error:', error);
      });

    } else {
       console.log('[APP_INIT] initializeApp: Not in Cordova environment, skipping push setup.');
    }
     console.log('[APP_INIT] initializeApp: Before contactsModelService.getContacts()');
    await this.contactsModelService.getContacts(12000).catch(error => {
      console.warn('[APP_INIT] Restaurants are unavailable:', error);
    });
    console.log('[APP_INIT] initializeApp: After contactsModelService.getContacts()');

     console.log('[APP_INIT] initializeApp: Before catalogService.getCatalog()');
    await this.catalogService.getCatalog(12000);
    console.log('[APP_INIT] initializeApp: After catalogService.getCatalog()');

     console.log('[APP_INIT] initializeApp: Before userModelService.getProfile()');
    await this.userModelService.getProfile(12000);
     console.log('[APP_INIT] initializeApp: After userModelService.getProfile()');

    // если пользователь залогинен
    if (this.userModelService.isLogin()) {
      console.log('[APP_INIT] initializeApp: User is logged in. Before surveyService.getSurveysCount()');
      this.surveyService.getSurveysCount(12000);
      console.log('[APP_INIT] initializeApp: After surveyService.getSurveysCount()');
    } else {
       console.log('[APP_INIT] initializeApp: User is not logged in.');
    }

     console.log('[APP_INIT] initializeApp: Before initKeyboard()');
    await this.initKeyboard();
    console.log('[APP_INIT] initializeApp: After initKeyboard()');

    console.log('[APP_INIT] initializeApp: Setting appReady to true');
    this.userModelService.updateMenuItems();
    this.appService.appReady = true;
    if (this.appService.isCordova() && this.catalogService.settings?.version) {
      this.checkAppVersion((result) => {
        this.update = result;
        this.checkForLoading();
      }).catch(error => console.warn('[APP_INIT] Version check failed:', error));
    }
    this.modalService.openMainPopup().catch(error =>
      console.warn('[APP_INIT] Main popup failed:', error));
     console.log('[APP_INIT] initializeApp: End');
    } catch (error) {
      console.error('[APP_INIT] Startup failed:', error);
      this.appService.isLoading = false;
      // @ts-ignore
      navigator.splashscreen?.hide();
      this.modalService.openErrorModal({
        title: 'Ошибка загрузки',
        text: 'Не удалось загрузить данные. Проверьте соединение и попробуйте снова',
        confirmText: 'Попробовать снова',
        cannotBeClosed: true,
        internetError: true
      }, (retry) => {
        this.appService.isLoading = false;
        if (retry) {
          this.initializeApp();
        }
      }).catch(modalError => console.error('[APP_INIT] Error modal failed:', modalError));
    } finally {
      this.startupLoading = false;
    }
  }

  startCheckOpenApp() {
    console.log('[APP_INIT] startCheckOpenApp: Start');
    this.appIsOpenInterval = setInterval(()=> {
      this.appIsOpenTime ++;
      if (this.appIsOpenTime >= 5) {
        this.appIsOpen = true;
      }
    }, 1000);
     console.log('[APP_INIT] startCheckOpenApp: End');
  }

  async checkAppVersion(callback: (result: {needUpdate: boolean; version: any}) => void) {
    console.log('[APP_INIT] checkAppVersion: Start');
    const data = await this.catalogService.settings.version;
    console.log('[APP_INIT] checkAppVersion: Got version data', data);
    if (parseFloat(data.critical) !== 0) {
      // приводим версию приложения для обновления и текущую к единому виду, чтобы сравнить
      const ver = this.appService.isIos() ?
        data.ios.replace(/\./g, '') :
        data.android.replace(/\./g, '');
      const versionCode = String(this.appService.appVersionNumber).replace(/\./g, '');
      // проверяем доступно ли обновление
      callback({
        needUpdate: parseFloat(ver) > parseFloat(versionCode),
        version: data
      });
    } else {
      console.log('[APP_INIT] checkAppVersion: Critical version is 0.');
      callback({
        needUpdate: false,
        version: data
      });
    }
    console.log('[APP_INIT] checkAppVersion: End');
  }

  checkForLoading() {
    console.log('[APP_INIT] checkForLoading: Start', 'update:', this.update);
    // если обновление доступно, от открывается модалка
    // в зависимости от того критично обновление или нет
    // backdropDismiss true или false
    if (this.update.needUpdate) {
      this.modalService.makeOpenModal({
        component: UpdateModalComponent,
        componentProps: {version: this.update.version},
        backdropDismiss: this.update.version.critical.toString() !== '2',
        showBackdrop: true,
        cssClass: `modal-select`,
        mode: 'md',
      }, (answer) => {});
    } else {
      this.navigationService.goTo('dashboard', {});
    }
    console.log('[APP_INIT] checkForLoading: End');
  }

  async checkInternet() {
    console.log('[APP_INIT] checkInternet: Start. navigator.onLine:', navigator.onLine);
    if (!navigator.onLine) {
      console.log('[APP_INIT] checkInternet: Offline. Hiding splashscreen and showing error modal.');
      // @ts-ignore
      navigator.splashscreen?.hide();
      await this.modalService.openErrorModal({
        title: 'Отсутствует интернет соединение',
        text: 'Проверьте соединение и попробуйте подключиться заново',
        confirmText: 'Попробовать снова',
        cannotBeClosed: true,
        internetError: true
      }, (res) => {
        if (res) {
          if (!navigator.onLine) {
            // @ts-ignore
            navigator.splashscreen.show();
            this.modalService.closeModalByClass('modal-error');
            setTimeout(()=> {
              this.checkInternet();
            }, 1500);
          } else {
            this.modalService.closeModalByClass('modal-error');
          }
        }
      });
    } else {
      console.log('[APP_INIT] checkInternet: Online. Returning.');
      return;
    }
    console.log('[APP_INIT] checkInternet: End');
  }

  goTo(link: string) {
    console.log('[APP_INIT] goTo:', link);
    if (link === 'about') {
      this.appService.openLink(this.catalogService.settings.aboutUs);
    } else {
      this.navigationService.goTo(link);
      this.appService.closeMenu();
    }
    console.log('[APP_INIT] goTo: End');
  }

  private initKeyboard() {
    console.log('[APP_INIT] initKeyboard: Start');
    window.addEventListener('keyboardWillShow', () => {
      this.cordovaService.keyBoardShow = true;
      this.cdr.detectChanges();
    });

    window.addEventListener('keyboardDidHide', () => {
      this.cordovaService.keyBoardShow = false;
      this.cdr.detectChanges();
    });
    console.log('[APP_INIT] initKeyboard: End');
  }
}
